import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import moment from 'moment';
import { CallDataRecord } from './dashboardTypes';
import {
    sendSessionInsightsChat,
    SessionInsightsChatMessage,
    SessionInsightsChatRequest,
    SessionInsightsChatResponse
} from '../../../../../shared/api/controllers/sessionInsights';
import {
    color_defaults_white,
    color_shades_darker,
    color_shades_darkest,
    color_shades_light,
    color_shades_lighter,
    voicify_spark_darkest,
    voicify_spark_dark,
    voicify_spark_light,
    voicify_spark_lightest
} from '../../../../../shared/constants/colors';

interface ChatEntry {
    message: SessionInsightsChatMessage;
    response?: SessionInsightsChatResponse;
    isError?: boolean;
    /** Groups a question's answer with its "load more" batches */
    chainId?: number;
    /** History that produced the answer (ending with the question), resent by "load more" */
    questionHistory?: SessionInsightsChatMessage[];
}

export interface SessionInsightsChatProps {
    isOpen: boolean;
    onClose: () => void;
    /** The calls the user is currently looking at; questions are answered against these only */
    records: CallDataRecord[];
    startDate: string;
    endDate: string;
    /** Dashboard the modal belongs to; tailors the prompts and example questions */
    domain: string;
    /** Restrict the modal's table to these session IDs */
    onShowMatches: (sessionIds: string[]) => void;
}

const EXAMPLE_QUESTIONS: Record<string, string[]> = {
    FOOD_ORDERING: [
        'Find calls where someone tried to order a BYO bread',
        'Which callers asked about catering?'
    ],
    HEALTHCARE: [
        'Find calls where a patient asked about insurance',
        'Which patients wanted to cancel, and why?',
        'Show calls where the patient asked for a person'
    ],
    OUTBOUND_CALLING: [
        'Which people asked to be removed from the list?',
        'Find calls that went to voicemail',
        'Show calls where someone wanted to reschedule'
    ]
};

// Status lines shown while a request runs, advancing roughly in step with the backend's stages
const ASK_LOADING_STEPS = ['Finding calls that match your question', 'Reading the matching transcripts', 'Reviewing the calls with AI'];
const LOAD_MORE_LOADING_STEPS = ['Loading the next batch of transcripts', 'Reviewing the calls with AI'];
const LOADING_STEP_INTERVAL_MS = 2500;

const LoadingIndicator: React.FC<{ steps: string[] }> = ({ steps }) => {
    const [step, setStep] = useState(0);
    useEffect(() => {
        setStep(0);
        const timer = setInterval(() => setStep(current => Math.min(current + 1, steps.length - 1)), LOADING_STEP_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [steps]);

    return (
        <Thinking role="status" aria-live="polite">
            <TypingDots aria-hidden="true"><span /><span /><span /></TypingDots>
            <ThinkingText key={step}>{steps[step]}…</ThinkingText>
        </Thinking>
    );
};

/**
 * Identifies the set of calls a chat is about. The modals stay mounted while hidden, so when the dates, domain or
 * call list change the chat (and any AI match filter) must reset instead of paging the old search over new calls.
 */
export const getSessionScopeKey = (records: CallDataRecord[], startDate: string, endDate: string, domain: string): string => {
    let hash = 5381;
    records.forEach(record => {
        const id = record.sessionId || '';
        for (let i = 0; i < id.length; i++) {
            hash = ((hash << 5) + hash + id.charCodeAt(i)) | 0;
        }
    });
    return `${domain}|${startDate}|${endDate}|${records.length}|${hash}`;
};

/**
 * SessionInsightsChat - a side panel inside the View Details modals for asking questions about the listed calls.
 * The backend keyword-searches the transcripts and has Gemini review the best matches, 40 at a time.
 */
const SessionInsightsChat: React.FC<SessionInsightsChatProps> = ({
    isOpen,
    onClose,
    records,
    startDate,
    endDate,
    domain,
    onShowMatches
}) => {
    const [entries, setEntries] = useState<ChatEntry[]>([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    // Chain whose "Review next" batch is loading; null while a new question is loading
    const [loadingMoreChainId, setLoadingMoreChainId] = useState<number | null>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const nextChainId = useRef(0);
    const exampleQuestions = EXAMPLE_QUESTIONS[domain] || EXAMPLE_QUESTIONS.FOOD_ORDERING;

    const recordsBySessionId = useMemo(() => {
        const map = new Map<string, CallDataRecord>();
        records.forEach(record => {
            if (record.sessionId && !map.has(record.sessionId)) map.set(record.sessionId, record);
        });
        return map;
    }, [records]);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }, [entries, isLoading]);

    // New call scope: start a fresh conversation and ignore any response still in flight for the old one
    const scopeKey = useMemo(() => getSessionScopeKey(records, startDate, endDate, domain), [records, startDate, endDate, domain]);
    const scopeGeneration = useRef(0);
    useEffect(() => {
        scopeGeneration.current++;
        setEntries([]);
        setIsLoading(false);
        setLoadingMoreChainId(null);
    }, [scopeKey]);

    const send = useCallback(async (history: SessionInsightsChatMessage[], chainId: number, page?: Pick<SessionInsightsChatRequest, 'offset' | 'searchPhrases'>) => {
        const generation = scopeGeneration.current;
        setIsLoading(true);
        const result = await sendSessionInsightsChat({
            sessions: [...recordsBySessionId.values()].map(r => ({ sessionId: r.sessionId, applicationId: r.applicationId })),
            startDate: moment(startDate).startOf('day').toISOString(),
            endDate: moment(endDate).endOf('day').toISOString(),
            messages: history,
            domain,
            ...page
        });
        if (generation !== scopeGeneration.current) return;
        setIsLoading(false);
        setLoadingMoreChainId(null);

        if (result.resultType === 'Ok' && result.data) {
            setEntries(prev => [...prev, { message: { role: 'assistant', text: result.data.answer }, response: result.data, chainId, questionHistory: history }]);
        } else {
            // The API reports its own failures as 400s with a message. "Unexpected" means no usable response came back:
            // a gateway error or timeout (HTTP status known), or a network failure (none), and errors[0] is only a generic message
            const error = result.resultType === 'Unexpected'
                ? (result.httpStatus
                    ? `The server could not finish answering (HTTP ${result.httpStatus}).`
                    : 'No response from the server. The request may have timed out or the connection dropped.')
                    + ' Please try again. If it keeps failing, narrow the date range or filters.'
                : result.errors?.[0]
                    || (result.resultType === 'Ok' ? 'The server returned an empty response.' : 'Something went wrong. Please try again.')
                    + (result.httpStatus ? ` (HTTP ${result.httpStatus})` : ' (no response from server)');
            setEntries(prev => [...prev, { message: { role: 'assistant', text: error }, isError: true, chainId }]);
        }
    }, [recordsBySessionId, startDate, endDate, domain]);

    const ask = useCallback((question: string) => {
        const trimmed = question.trim();
        if (!trimmed || isLoading) return;

        const userMessage: SessionInsightsChatMessage = { role: 'user', text: trimmed };
        const history = [...entries.filter(e => !e.isError).map(e => e.message), userMessage];
        const chainId = nextChainId.current++;
        setEntries(prev => [...prev, { message: userMessage, chainId }]);
        setInput('');
        send(history, chainId);
    }, [entries, isLoading, send]);

    const loadMore = useCallback((entry: ChatEntry) => {
        if (isLoading || !entry.response?.hasMore || !entry.questionHistory) return;
        setLoadingMoreChainId(entry.chainId);
        send(entry.questionHistory, entry.chainId, { offset: entry.response.nextOffset, searchPhrases: entry.response.searchPhrases });
    }, [isLoading, send]);

    // All matches found so far for a question, across its "load more" batches
    const getChainMatchIds = (chainId: number, upToIndex: number) => [...new Set(
        entries.slice(0, upToIndex + 1)
            .filter(e => e.chainId === chainId && e.response)
            .reduce<string[]>((ids, e) => ids.concat(e.response.matches.map(m => m.sessionId)), [])
    )];

    // Only a later successful batch supersedes this one, so a failed "load more" can be retried from here
    const isLastInChain = (index: number) => !entries.slice(index + 1).some(e => e.chainId === entries[index].chainId && e.response);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            ask(input);
        }
    };

    const buildTranscriptUrl = (applicationId: string, sessionId: string) =>
        `/v/apps/${applicationId}/analytics/sessions?sessionId=${sessionId}&startDate=${encodeURIComponent(moment(startDate).startOf('day').toISOString())}&endDate=${encodeURIComponent(moment(endDate).endOf('day').toISOString())}`;

    if (!isOpen) return null;

    return (
        <Panel>
            <PanelHeader>
                <PanelTitle><AiBadge>AI</AiBadge> Ask about these calls</PanelTitle>
                <HeaderActions>
                    {entries.length > 0 && (
                        <TextButton onClick={() => setEntries([])} disabled={isLoading}>New chat</TextButton>
                    )}
                    <CloseButton onClick={onClose} aria-label="Close AI chat">×</CloseButton>
                </HeaderActions>
            </PanelHeader>

            <Messages ref={scrollRef}>
                {entries.length === 0 && (
                    <EmptyState>
                        <p>Ask a question about the {recordsBySessionId.size.toLocaleString()} calls in this list. I'll search the transcripts and point you to the matching calls.</p>
                        {exampleQuestions.map(q => (
                            <ExampleButton key={q} onClick={() => ask(q)}>{q}</ExampleButton>
                        ))}
                    </EmptyState>
                )}

                {entries.map((entry, index) => {
                    const response = entry.response;
                    const isContinuation = response?.offset > 0;
                    const chainMatchIds = response ? getChainMatchIds(entry.chainId, index) : [];
                    return (
                        <Bubble key={index} isUser={entry.message.role === 'user'} isError={entry.isError}>
                            {isContinuation && (
                                <BatchLabel>Matches {response.offset + 1}–{response.nextOffset} of {response.candidateCount.toLocaleString()}</BatchLabel>
                            )}
                            <div>{entry.message.text}</div>
                            {response?.matches?.length > 0 && (
                                <MatchList>
                                    {response.matches.map(match => {
                                        const record = recordsBySessionId.get(match.sessionId);
                                        return (
                                            <MatchItem key={match.sessionId}>
                                                <MatchMeta>
                                                    {record ? `${record.date} ${record.time} · ${record.location}` : match.sessionId}
                                                </MatchMeta>
                                                <MatchReason>{match.reason}</MatchReason>
                                                <a href={buildTranscriptUrl(match.applicationId, match.sessionId)} target="_blank" rel="noopener noreferrer">
                                                    View Transcript
                                                </a>
                                            </MatchItem>
                                        );
                                    })}
                                </MatchList>
                            )}
                            {chainMatchIds.length > 0 && (
                                <TextButton onClick={() => onShowMatches(chainMatchIds)}>
                                    {isContinuation
                                        ? `Show all ${chainMatchIds.length} matches so far in the table`
                                        : `Show these ${chainMatchIds.length} calls in the table`}
                                </TextButton>
                            )}
                            {response?.hasMore && isLastInChain(index) && (
                                <PartialReviewNotice>
                                    <span>
                                        {response.candidateCount.toLocaleString()} calls mentioned these keywords and {response.nextOffset} have been reviewed so far, so there may be more matches.
                                    </span>
                                    <LoadMoreButton onClick={() => loadMore(entry)} disabled={isLoading}>
                                        {loadingMoreChainId === entry.chainId
                                            ? <><MiniSpinner aria-hidden="true" /> Reviewing…</>
                                            : <>Review next {Math.min(40, response.candidateCount - response.nextOffset)} ↓</>}
                                    </LoadMoreButton>
                                </PartialReviewNotice>
                            )}
                            {response && (
                                <Footnote>
                                    Searched for {response.searchPhrases.map(p => `"${p}"`).join(', ')} · reviewed {response.sessionsReviewed} of {response.candidateCount.toLocaleString()} keyword matches{isContinuation ? ' in this batch' : ''}
                                </Footnote>
                            )}
                        </Bubble>
                    );
                })}

                {isLoading && (
                    <Bubble isUser={false}>
                        <LoadingIndicator steps={loadingMoreChainId !== null ? LOAD_MORE_LOADING_STEPS : ASK_LOADING_STEPS} />
                    </Bubble>
                )}
            </Messages>

            <InputRow>
                <ChatInput
                    rows={2}
                    placeholder={`e.g. ${exampleQuestions[0]}`}
                    value={input}
                    maxLength={1000}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isLoading}
                />
                <SendButton onClick={() => ask(input)} disabled={isLoading || !input.trim()}>Ask</SendButton>
            </InputRow>
        </Panel>
    );
};
export default SessionInsightsChat;

// ==================== STYLES ====================

export const AiChatToggleButton = styled.button<{ isActive?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    border-radius: 16px;
    border: 1px solid ${voicify_spark_darkest};
    background: ${props => props.isActive ? voicify_spark_darkest : color_defaults_white};
    color: ${props => props.isActive ? color_defaults_white : voicify_spark_darkest};
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: ${props => props.isActive ? voicify_spark_dark : voicify_spark_lightest};
    }
`;

const Panel = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 400px;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    background: ${color_defaults_white};
    border-left: 1px solid ${color_shades_light};
    box-shadow: -8px 0 24px rgba(50, 70, 97, 0.12);
    z-index: 20;
    animation: sessionInsightsPanelIn 220ms cubic-bezier(0.2, 0.8, 0.2, 1);

    @keyframes sessionInsightsPanelIn {
        from {
            opacity: 0;
            transform: translateX(24px);
        }
        to {
            opacity: 1;
            transform: translateX(0);
        }
    }

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

const PanelHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    border-bottom: 1px solid ${color_shades_light};
`;

const PanelTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 600;
    color: ${color_shades_darkest};
`;

const AiBadge = styled.span`
    background: ${voicify_spark_darkest};
    color: ${color_defaults_white};
    font-size: 10px;
    font-weight: 700;
    padding: 2px 6px;
    border-radius: 4px;
    letter-spacing: 0.5px;
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 22px;
    line-height: 1;
    color: ${color_shades_darker};
    cursor: pointer;

    &:hover {
        color: ${color_shades_darkest};
    }
`;

const TextButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    font-size: 12px;
    font-weight: 600;
    color: ${voicify_spark_darkest};
    cursor: pointer;
    text-align: left;

    &:hover:not(:disabled) {
        text-decoration: underline;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const Messages = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: ${color_shades_lighter};
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    font-size: 13px;
    color: ${color_shades_darkest};

    p {
        margin: 0 0 4px;
        line-height: 1.5;
    }
`;

const ExampleButton = styled.button`
    text-align: left;
    padding: 8px 12px;
    border: 1px solid ${color_shades_light};
    border-radius: 8px;
    background: ${color_defaults_white};
    font-size: 13px;
    color: ${color_shades_darkest};
    cursor: pointer;

    &:hover {
        border-color: ${voicify_spark_dark};
        background: ${voicify_spark_lightest};
    }
`;

const Bubble = styled.div<{ isUser: boolean; isError?: boolean }>`
    align-self: ${props => props.isUser ? 'flex-end' : 'flex-start'};
    max-width: 92%;
    padding: 10px 12px;
    border-radius: 10px;
    font-size: 13px;
    line-height: 1.5;
    white-space: pre-wrap;
    display: flex;
    flex-direction: column;
    gap: 8px;
    background: ${props => props.isUser ? voicify_spark_darkest : color_defaults_white};
    color: ${props => props.isUser ? color_defaults_white : props.isError ? '#B42318' : color_shades_darkest};
    border: ${props => props.isUser ? 'none' : `1px solid ${color_shades_light}`};
`;

const MatchList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: 320px;
    overflow-y: auto;
`;

const MatchItem = styled.div`
    padding: 8px 10px;
    border-radius: 6px;
    background: ${voicify_spark_lightest};
    display: flex;
    flex-direction: column;
    gap: 2px;
    white-space: normal;

    a {
        font-size: 12px;
        font-weight: 600;
        color: ${voicify_spark_darkest};
    }
`;

const MatchMeta = styled.div`
    font-size: 12px;
    font-weight: 600;
`;

const MatchReason = styled.div`
    font-size: 12px;
`;

const PartialReviewNotice = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 8px;
    background: #FFF8E6;
    border: 1px solid #F5D78E;
    color: #7A5B00;
    font-size: 12px;
    line-height: 1.45;
    white-space: normal;
`;

const LoadMoreButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    border: 1px solid ${voicify_spark_darkest};
    border-radius: 16px;
    background: ${color_defaults_white};
    color: ${voicify_spark_darkest};
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        background: ${voicify_spark_lightest};
    }

    &:disabled {
        cursor: default;
        opacity: 0.7;
    }
`;

const MiniSpinner = styled.span`
    width: 10px;
    height: 10px;
    border: 2px solid ${voicify_spark_light};
    border-top-color: ${voicify_spark_darkest};
    border-radius: 50%;
    animation: sessionInsightsSpin 0.8s linear infinite;

    @keyframes sessionInsightsSpin {
        to {
            transform: rotate(360deg);
        }
    }
`;

const BatchLabel = styled.div`
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    color: ${color_shades_darker};
`;

const Footnote = styled.div`
    font-size: 11px;
    color: ${color_shades_darker};
    white-space: normal;
`;

const Thinking = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    color: ${color_shades_darker};
`;

const TypingDots = styled.span`
    display: inline-flex;
    gap: 4px;

    span {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: ${voicify_spark_darkest};
        animation: sessionInsightsDot 1.2s ease-in-out infinite;
    }

    span:nth-of-type(2) {
        animation-delay: 0.15s;
    }

    span:nth-of-type(3) {
        animation-delay: 0.3s;
    }

    @keyframes sessionInsightsDot {
        0%, 60%, 100% {
            opacity: 0.3;
            transform: translateY(0);
        }
        30% {
            opacity: 1;
            transform: translateY(-3px);
        }
    }

    @media (prefers-reduced-motion: reduce) {
        span {
            animation: none;
            opacity: 0.6;
        }
    }
`;

const ThinkingText = styled.span`
    animation: sessionInsightsFadeIn 300ms ease;

    @keyframes sessionInsightsFadeIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }
`;

const InputRow = styled.div`
    display: flex;
    gap: 8px;
    padding: 12px 16px;
    border-top: 1px solid ${color_shades_light};
    align-items: flex-end;
`;

const ChatInput = styled.textarea`
    flex: 1;
    resize: none;
    border: 1px solid ${color_shades_light};
    border-radius: 8px;
    padding: 8px 10px;
    font-family: inherit;
    font-size: 13px;
    color: ${color_shades_darkest};

    &:focus {
        outline: none;
        border-color: ${voicify_spark_dark};
    }
`;

const SendButton = styled.button`
    padding: 9px 16px;
    border: none;
    border-radius: 18px;
    background: ${voicify_spark_darkest};
    color: ${color_defaults_white};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: ${voicify_spark_dark};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;
